import { loadQuestionsForLanguage } from '../data/passFirstExamQuestions';
import type { Question } from '../types/passFirstExam';

export interface WeakAreaData {
  topics: string[];
  scores: Record<string, number>;
}

const STORAGE_KEY = 'aiExamWeakAreas';
const PRACTICE_DONE_KEY = 'aiExamPracticeDone';

export function saveWeakAreas(data: WeakAreaData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  localStorage.removeItem(PRACTICE_DONE_KEY);
}

export function getWeakAreas(): WeakAreaData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function markPracticeDone(): void {
  localStorage.setItem(PRACTICE_DONE_KEY, 'true');
}

export function isPracticeDone(): boolean {
  return localStorage.getItem(PRACTICE_DONE_KEY) === 'true';
}

export function canRetakeExam(): boolean {
  return isPracticeDone();
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function buildWeakAreaQuestions(
  lang: 'NL' | 'EN' | 'AR',
  totalCount: number
): Question[] {
  const all = loadQuestionsForLanguage(lang);
  if (!all || all.length === 0) return [];

  const weakData = getWeakAreas();
  if (!weakData || weakData.topics.length === 0) {
    return shuffle(all).slice(0, totalCount);
  }

  const weakTopics = new Set(weakData.topics.map(t => t.toLowerCase()));

  const weakQs: Question[] = [];
  const otherQs: Question[] = [];

  for (const q of all) {
    const topic = (q.topic || q.subject || '').toLowerCase();
    if (weakTopics.has(topic)) {
      weakQs.push(q);
    } else {
      otherQs.push(q);
    }
  }

  const weakCount = Math.round(totalCount * 0.6);
  const otherCount = totalCount - weakCount;

  const selectedWeak = shuffle(weakQs).slice(0, weakCount);
  const selectedOther = shuffle(otherQs).slice(0, otherCount);

  const remaining = totalCount - selectedWeak.length - selectedOther.length;
  let filler: Question[] = [];
  if (remaining > 0) {
    const usedIds = new Set([...selectedWeak, ...selectedOther].map(q => q.id));
    filler = shuffle(all.filter(q => !usedIds.has(q.id))).slice(0, remaining);
  }

  return shuffle([...selectedWeak, ...selectedOther, ...filler]);
}
